import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  ApiCreateQuestPayload,
  ApiQuest,
  ApiQuestCostEstimatePayload,
  ApiQuestDetail,
  ApiQuestEntryStaff,
  ApiUpdateQuestPayload,
} from "griptonite-api-client/types";

import {
  HIDEABLE_WARNING_TABS,
  hideWarningsKey,
} from "@/components/QuestProfile/getPublishWarnings";
import api from "@/core/api";
import queryClient from "@/core/queryClient";

// Only the first page (50 quests), as with challenges. The API orders active, then upcoming, then
// ended (most recently ended first), so a truncated page loses the oldest ended quests.
export const useVenueQuestsQuery = (venueUuid?: string) =>
  useQuery({
    queryKey: ["venues", venueUuid, "quests"],
    queryFn: async () => (await api.venues.getVenueQuests(venueUuid!)).data,
    enabled: !!venueUuid,
  });

const findQuestInCache = (venueUuid: string, uuid: string) => {
  const cached = queryClient.getQueryData<ApiQuest[]>(["venues", venueUuid, "quests"]);
  return cached?.find((quest) => quest.uuid === uuid);
};

// The list entry stands in until the detail arrives, so `pointsThresholds` is only there once
// the detail has.
export const useQuestQuery = (questUuid?: string, venueUuid?: string) =>
  useQuery<ApiQuest | ApiQuestDetail>({
    queryKey: ["quests", questUuid],
    queryFn: () => api.quests.getQuest(questUuid!),
    enabled: !!questUuid,
    placeholderData: () =>
      venueUuid && questUuid ? findQuestInCache(venueUuid, questUuid) : undefined,
  });

// A new draft starts without publish warnings, since none of its tabs have been visited yet.
const cacheNewQuest = (quest: ApiQuestDetail, venueUuid: string) => {
  HIDEABLE_WARNING_TABS.forEach((tab) =>
    localStorage.setItem(hideWarningsKey(quest.uuid, tab), "true"),
  );
  queryClient.setQueryData(["quests", quest.uuid], quest);
  // The list's place for the quest depends on its status, so it's refetched, not patched.
  queryClient.invalidateQueries({ queryKey: ["venues", venueUuid, "quests"] });
};

export const useCreateQuestMutation = () =>
  useMutation({
    mutationFn: (payload: ApiCreateQuestPayload) => api.quests.createQuest(payload),
    onSuccess: (quest, payload) => cacheNewQuest(quest, payload.venue),
  });

export const useDuplicateQuestMutation = () =>
  useMutation({
    mutationFn: (questUuid: string) => api.quests.duplicateQuest(questUuid),
    onSuccess: (quest) => cacheNewQuest(quest, quest.venue.uuid),
  });

export const useDeleteQuestMutation = (venueUuid?: string) =>
  useMutation({
    mutationFn: (questUuid: string) => api.quests.deleteQuest(questUuid),
    onSuccess: (_, questUuid) => {
      queryClient.removeQueries({ queryKey: ["quests", questUuid] });
      if (venueUuid) {
        queryClient.invalidateQueries({ queryKey: ["venues", venueUuid, "quests"] });
      }
    },
  });

export const useUpdateQuestMutation = (questUuid?: string, venueUuid?: string) =>
  useMutation({
    mutationFn: (payload: ApiUpdateQuestPayload) => api.quests.updateQuest(questUuid!, payload),
    onSuccess: (quest) => {
      queryClient.setQueryData(["quests", quest.uuid], quest);
      if (venueUuid) {
        // The list is ordered by status, so a changed period can move the quest between groups.
        // Its entries also carry the quest's boosts and bonuses.
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "quests"],
        });
      }
    },
  });

// Keeps showing the last estimate while the next one loads, so the preview doesn't flash empty as
// the settings change.
export const useQuestCostEstimateQuery = (payload?: ApiQuestCostEstimatePayload) =>
  useQuery({
    queryKey: ["quests", "cost-estimate", payload],
    queryFn: () => api.quests.estimateQuestCost(payload!),
    enabled: !!payload,
    placeholderData: keepPreviousData,
  });

// Only points quests have stats; the API 404s for the other types.
export const useQuestStatsQuery = (questUuid?: string, enabled = true) =>
  useQuery({
    queryKey: ["quests", questUuid, "stats"],
    queryFn: () => api.quests.getQuestStats(questUuid!),
    enabled: !!questUuid && enabled,
  });

// Every page, since the entries table searches and sorts in the browser.
export const useQuestEntriesQuery = (questUuid?: string) =>
  useQuery({
    queryKey: ["quests", questUuid, "entries"],
    queryFn: () =>
      api.collectPages<ApiQuestEntryStaff>(() => api.quests.getQuestEntries(questUuid!)),
    enabled: !!questUuid,
  });

// A page at a time, newest first, since an active climber's log can run to hundreds of rows.
export const useQuestEntryLogsQuery = (questUuid?: string, entryUuid?: string) =>
  useInfiniteQuery({
    queryKey: ["quests", questUuid, "entries", entryUuid, "logs"],
    queryFn: ({ pageParam }) =>
      api.quests.getQuestEntryLogs(
        questUuid!,
        entryUuid!,
        new URLSearchParams({ page: String(pageParam) }),
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.metadata.next ? lastPageParam + 1 : undefined,
    enabled: !!questUuid && !!entryUuid,
  });

// The disqualification runs in the background after the ban, so the refetch can come back before
// the clawback shows in the entry and its log.
export const useBanFromLeaderboardMutation = (venueUuid: string, questUuid: string) =>
  useMutation({
    mutationFn: (userUuid: string) => api.venues.banFromLeaderboard(venueUuid, userUuid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["quests", questUuid] }),
  });
